import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import theme from './styles/theme';
import { AuthProvider } from './context/AuthContext';
import ProtectedRoute from './components/common/ProtectedRoute';
import Layout from './components/layout/Layout';
import Callback from './pages/auth/Callback';
import Login from './pages/auth/Login';
import Dashboard from './pages/dashboard/Dashboard';
import CustomerRequest from './pages/customerRequest/CustomerRequest.js';
import CustomerRequestForm from './pages/customerRequest/CustomerRequestForm.js';
import CustomerRequestDetails from './pages/customerRequest/CustomerRequestDetails.js';

import Reports from './pages/reports/Reports';
import Admin from './pages/admin/Admin';
import DeliveryRequest from './pages/deliveryRequest/DeliveryRequest';
import DeliveryRequestDetails from './pages/deliveryRequest/DeliveryRequestDetails';
import Effort from './pages/effort/Effort';
import EffortDetail from './pages/effort/EffortDetail';
import WorkOrder from './pages/workOrder/WorkOrder';
import WorkOrderDetails from './pages/workOrder/WorkOrderDetails';
import JobLines from './pages/admin/jobLines/JobLines';
import PersonInCharge from './pages/admin/personInCharge/PersonInCharge';
import SonyEmployees from './pages/admin/sonyEmployees/SonyEmployees';
import CarsonHeadline from './pages/admin/carsonHeadline/CarsonHeadline';
import Departments from './pages/admin/departments/Departments';
import BudgetCode from './pages/admin/budgetCode/BudgetCode';
import TempLabor from './pages/admin/tempLabor/TempLabor';
import ModelUpload from './pages/admin/modelUpload/ModelUpload';
import Models from './pages/admin/models/Models';
import WorkType from './pages/admin/workType/WorkType';
import EmailSetup from './pages/admin/emailSetup/EmailSetup';
import BudgetCodeUpload from './pages/admin/budgetCodeUpload/BudgetCodeUpload';
import ContactUsPage from './pages/contact/ContactUsPage';
import DetailedInvoice from './components/DetailedInvoice';
import DetailedInvoiceDetails from './pages/detailedInvoice/DetailedInvoiceDetails';
import { SummaryInvoice, SummaryInvoiceDetails } from './pages/summaryInvoice';


import './config/amplify';
import './styles/dataGridHeaders.css';
import './styles/accessibility.css';

// Available API Gateway Endpoints:
// /api/auth - Authentication endpoints
// /api/users - User management endpoints  
// /api/projects - Project/Customer Request endpoints
// /api/activity-report - Activity report endpoints
// Future endpoints to be implemented:
// /api/invoice/* - Invoice related endpoints
// /api/admin/* - Admin panel endpoints

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/callback" element={<Callback />} />
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Dashboard />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Dashboard />
                  </Layout>
                </ProtectedRoute>
              }
            />            
            <Route
              path="/customer-request"
              element={
                <ProtectedRoute>
                  <Layout>
                    <CustomerRequest />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/customer-request/:id"
              element={
                <ProtectedRoute>
                  <Layout>
                    <CustomerRequestDetails />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/customer-request/:id/edit"
              element={
                <ProtectedRoute>
                  <Layout>
                    <CustomerRequestForm />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/add-customer-request-form"
              element={
                <ProtectedRoute>
                  <Layout>
                    <CustomerRequestForm />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/contact"
              element={
                <ProtectedRoute>
                  <Layout>
                    <ContactUsPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/contact-us"
              element={
                <ProtectedRoute>
                  <Layout>
                    <ContactUsPage />
                  </Layout>
                </ProtectedRoute>
              }
            />                      
            <Route
              path="/reports"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Reports />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Admin />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/delivery-request"
              element={
                <ProtectedRoute>
                  <Layout>
                    <DeliveryRequest />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/delivery-request/:projectCode"
              element={
                <ProtectedRoute>
                  <Layout>
                    <DeliveryRequestDetails />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/effort"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Effort />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/effort/detail/:projectCode"
              element={
                <ProtectedRoute>
                  <Layout>
                    <EffortDetail />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/work-order"
              element={
                <ProtectedRoute>
                  <Layout>
                    <WorkOrder />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/work-order/:projectCode"
              element={
                <ProtectedRoute>
                  <Layout>
                    <WorkOrderDetails />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/invoice/detailed"
              element={
                <ProtectedRoute>
                  <Layout>
                    <DetailedInvoice />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/invoice/detailed/:projectCode"
              element={
                <ProtectedRoute>
                  <Layout>
                    <DetailedInvoiceDetails />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/invoice/summary"
              element={
                <ProtectedRoute>
                  <Layout>
                    <SummaryInvoice />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/summary-invoice"
              element={
                <ProtectedRoute>
                  <Layout>
                    <SummaryInvoice />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/summary-invoice/:projectCode"
              element={
                <ProtectedRoute>
                  <Layout>
                    <SummaryInvoiceDetails />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/budget-code"
              element={
                <ProtectedRoute>
                  <Layout>
                    <BudgetCode />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/model"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Models />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/job-lines"
              element={
                <ProtectedRoute>
                  <Layout>
                    <JobLines />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/carson-headline"
              element={
                <ProtectedRoute>
                  <Layout>
                    <CarsonHeadline />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/person-in-charge"
              element={
                <ProtectedRoute>
                  <Layout>
                    <PersonInCharge />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/sony-employee"
              element={
                <ProtectedRoute>
                  <Layout>
                    <SonyEmployees />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/temp-labor"
              element={
                <ProtectedRoute>
                  <Layout>
                    <TempLabor />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/email-setup"
              element={
                <ProtectedRoute>
                  <Layout>
                    <EmailSetup />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/departments"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Departments />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/work-type"
              element={
                <ProtectedRoute>
                  <Layout>
                    <WorkType />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/budget-code-upload"
              element={
                <ProtectedRoute>
                  <Layout>
                    <BudgetCodeUpload />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/model-upload"
              element={
                <ProtectedRoute>
                  <Layout>
                    <ModelUpload />
                  </Layout>
                </ProtectedRoute>
              }
            />

          </Routes>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;